import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { fetchCoverage, buildCoverageChips, nextStepFor } from '../components/EntryFlowModal';
import { MonthLedger } from '../components/MonthLedger';
import { X } from '../components/v2/Icons';
import { EntryStepper } from '../components/v2/EntryStepper';
import { CategoryContext } from '../components/v2/CategoryContext';
import { PageHeader, Card, Button, Field, Input, Select, UnitInput, Callout, DList } from '../components/v2/ui';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Vehicle categories (module-level: resolved via stored language, not the hook)
const isEn = () => localStorage.getItem('k360-lang') === 'en';
const VEHICLE_CATEGORIES = [
  { value: 'fuel_vehicle', label: isEn() ? 'Fuel vehicle (diesel / petrol / LPG / CNG)' : 'Automjet me karburant (diesel / benzinë / LPG / CNG)' },
  { value: 'ev', label: isEn() ? 'Electric vehicle (EV)' : 'Automjet Elektrik (EV)' },
  { value: 'hybrid', label: isEn() ? 'Hybrid vehicle' : 'Automjet hybrid' }
];

// Mobile combustion fuels only
const MOBILE_FUEL_KEYS = ['diesel_mobile', 'gasoline_mobile', 'lpg_mobile', 'cng_mobile'];

// Fuel to unit mapping
const FUEL_DEFAULT_UNITS = {
  'diesel_mobile': 'l',
  'gasoline_mobile': 'l',
  'lpg_mobile': 'l',
  'cng_mobile': 'm3'
};

// Fuel labels for dropdown
const MOBILE_FUEL_LABELS = {
  'diesel_mobile': 'Diesel',
  'gasoline_mobile': isEn() ? 'Petrol' : 'Benzinë',
  'lpg_mobile': 'LPG',
  'cng_mobile': 'CNG'
};

// Distance-based vehicle types
const DISTANCE_VEHICLE_TYPES = [
  { value: 'passenger_car_distance', label: isEn() ? 'Passenger car' : 'Makinë (Passenger Car)' },
  { value: 'diesel_bus_distance', label: isEn() ? 'Diesel bus/truck (heavy duty)' : 'Autobus/Kamion Diesel (Heavy Duty)' }
];

export const Scope1MobilePage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  
  const [formData, setFormData] = useState({
    buildingId: '',
    vehicleCategory: '',
    fuelKey: '',
    period: getCurrentMonth(),
    quantity: '205',
    unit: 'l'
  });

  const [buildings, setBuildings] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [calculated, setCalculated] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [buildingValidationError, setBuildingValidationError] = useState('');
  
  // New state for conditional logic
  const [registrationMethod, setRegistrationMethod] = useState('fuel'); // 'fuel' or 'distance'
  const [distanceVehicleType, setDistanceVehicleType] = useState('passenger_car_distance');
  const [showHybridScope2Modal, setShowHybridScope2Modal] = useState(false);
  
  // Summary state for month comparison
  const [, setSummary] = useState({ current: null, previous: null });

  // Combined total for hybrid vehicles (Scope 1 fuel + Scope 2 EV charging)
  const [hybridCombined, setHybridCombined] = useState(null);

  // Vehicle ID state
  const [vehicleId, setVehicleId] = useState('');
  const [existingVehicles, setExistingVehicles] = useState([]);
  // Verifiko → Ruaj → Vazhdo dialog
  // Zgjidh vendin → Shto të dhënat → Rishiko & regjistro
  const [step, setStep] = useState(0);
  const [mode, setMode] = useState('manual');
  const [flowError, setFlowError] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedInvoice, setUploadedInvoice] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [ledgerKey, setLedgerKey] = useState(0);

  // Fetch combined hybrid totals (Scope 1 fuel + Scope 2 EV charging)
  // for the currently selected building + period.
  const fetchHybridCombined = async () => {
    if (!formData.buildingId || !formData.period) { setHybridCombined(null); return; }
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ buildingId: formData.buildingId, period: formData.period });
      const res = await fetch(`${API_BASE}/api/emissions/hybrid/combined?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setHybridCombined(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch hybrid combined:', err);
    }
  };

  // Fetch summary for month comparison
  const fetchSummary = async (period) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/emissions/scope1/mobile/summary?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/buildings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => setBuildings(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to fetch buildings:', err));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/fuels`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => res.json())
      .then(data => {
        // Filter to only show mobile combustion fuels
        const mobileFuels = (Array.isArray(data) ? data : [])
          .filter(f => MOBILE_FUEL_KEYS.includes(f.key));
        setFuels(mobileFuels);
      })
      .catch(err => console.error('Failed to fetch fuels:', err));
  }, []);

  // Fetch summary when period changes
  useEffect(() => {
    if (formData.period) {
      fetchSummary(formData.period);
    }
  }, [formData.period]);

  // Refresh hybrid combined total when building/period changes (or vehicle category
  // switches to hybrid). The endpoint is harmless to call even for non-hybrid contexts.
  useEffect(() => {
    if (formData.vehicleCategory === 'hybrid') {
      fetchHybridCombined();
    } else {
      setHybridCombined(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.buildingId, formData.period, formData.vehicleCategory]);

  // Fetch existing vehicles
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/emissions/scope1/mobile/vehicles`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => setExistingVehicles(Array.isArray(data) ? data : []))
      .catch(err => console.error('Failed to fetch vehicles:', err));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setIsSaved(false);
  };

  // Handle vehicle category change
  const handleVehicleCategoryChange = (category) => {
    setFormData(prev => ({ 
      ...prev, 
      vehicleCategory: category,
      fuelKey: '',
      unit: 'l'
    }));
    setIsSaved(false);
    setCalculated(null);
    setShowHybridScope2Modal(false);
  };

  // Handle fuel change with automatic unit selection
  const handleFuelChange = (fuelKey) => {
    const defaultUnit = FUEL_DEFAULT_UNITS[fuelKey] || 'l';
    setFormData(prev => ({ 
      ...prev, 
      fuelKey,
      unit: registrationMethod === 'distance' ? 'km' : defaultUnit 
    }));
    setIsSaved(false);
  };

  // Handle registration method change
  const handleMethodChange = (method) => {
    setRegistrationMethod(method);
    if (method === 'distance') {
      setFormData(prev => ({ ...prev, unit: 'km' }));
    } else {
      const defaultUnit = FUEL_DEFAULT_UNITS[formData.fuelKey] || 'l';
      setFormData(prev => ({ ...prev, unit: defaultUnit }));
    }
  };

  // Combined calculate and save function
  // Step 1: calculate only, then open the Verifiko → Ruaj → Vazhdo dialog
  const handleSaveEmissions = async () => {
    if (!formData.buildingId) {
      setBuildingValidationError(t('s1mob.pickBuildingFirst'));
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');

      if (!token) {
        alert(t('invp.sessionExpired'));
        window.location.href = '/';
        return;
      }

      // Determine the correct fuel key based on registration method
      const effectiveFuelKey = registrationMethod === 'distance'
        ? distanceVehicleType
        : formData.fuelKey;

      const calcResponse = await fetch(`${API_BASE}/api/emissions/scope1/mobile/calc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          vehicleType: formData.vehicleCategory,
          fuelKey: effectiveFuelKey,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit,
          registrationMethod: registrationMethod
        })
      });

      if (calcResponse.status === 401) {
        alert(t('invp.sessionExpired'));
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }

      if (!calcResponse.ok) throw new Error('Calculation failed');
      const calcData = await calcResponse.json();
      setCalculated(calcData);
      setBuildingValidationError('');
      setFlowError('');
      setStep(2);
    } catch (err) {
      setBuildingValidationError(t('invnp.errorWithMessage', { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  // Step 2: save from the dialog
  const performSave = async () => {
    if (!calculated) return false;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('invp.sessionExpired'));
        window.location.href = '/';
        return false;
      }

      const effectiveFuelKey = registrationMethod === 'distance'
        ? distanceVehicleType
        : formData.fuelKey;

      const saveResponse = await fetch(`${API_BASE}/api/emissions/scope1/mobile/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          buildingId: parseInt(formData.buildingId),
          vehicleType: formData.vehicleCategory,
          fuelKey: effectiveFuelKey,
          period: formData.period,
          quantity: parseFloat(formData.quantity),
          unit: formData.unit,
          registrationMethod: registrationMethod,
          energy_kWh: calculated.energy_kWh,
          emissions_tCO2e: calculated.emissions_tCO2e,
          vehicleId: vehicleId || null
        })
      });

      if (!saveResponse.ok) {
        const errorData = await saveResponse.json();
        throw new Error(errorData.message || 'Save failed');
      }

      setIsSaved(true);
      setBuildingValidationError('');

      // Refresh summary to show updated month data
      await fetchSummary(formData.period);

      // Refresh hybrid combined total if this was a hybrid save
      if (formData.vehicleCategory === 'hybrid') {
        await fetchHybridCombined();
      }

      // Refresh vehicles list if new vehicle was entered
      if (vehicleId) {
        const vToken = localStorage.getItem('token');
        fetch(`${API_BASE}/api/emissions/scope1/mobile/vehicles`, {
          headers: { 'Authorization': `Bearer ${vToken}` }
        })
          .then(res => res.ok ? res.json() : [])
          .then(data => setExistingVehicles(Array.isArray(data) ? data : []))
          .catch(() => {});
      }
      return true;
    } catch (err) {
      setBuildingValidationError(t('invnp.errorWithMessage', { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleFlowSave = async () => {
    setFlowError('');
    const ok = await performSave();
    if (!ok) {
      setFlowError(t('invp.saveRetry'));
      return;
    }
    setLedgerKey(k => k + 1);
    // Hybrid vehicles still get the Scope 2 charging prompt, but it now appears
    // inside step 3 alongside the coverage chips rather than replacing them —
    // previously the hybrid path skipped the success state entirely.
    if (formData.vehicleCategory === 'hybrid') {
      setShowHybridScope2Modal(true);
    }
    try {
      setCoverage(await fetchCoverage(formData.period));
    } catch (e) {
      setCoverage(null);
    }
  };

  const resetEntry = () => {
    setCalculated(null);
    setIsSaved(false);
    setVehicleId('');
    setFile(null);
    setUploadedInvoice(null);
    setUploadError('');
    setShowHybridScope2Modal(false);
    setFormData(prev => ({ ...prev, fuelKey: '', quantity: '' }));
  };

  const handleFlowAddAnother = () => { resetEntry(); setStep(1); };
  const handleFlowFinish = () => { resetEntry(); setStep(0); };

  /** Attach the source document — same endpoint the stationary and process screens use. */
  const handlePickFile = async (picked) => {
    if (!picked) return;
    if (picked.size > 10 * 1024 * 1024) { setUploadError(t('entry.uploadTooBig')); return; }
    if (!formData.buildingId) { setUploadError(t('entry.uploadNoBuilding')); return; }
    try {
      setUploading(true);
      setUploadError('');
      const fd = new FormData();
      fd.append('file', picked);
      fd.append('buildingId', formData.buildingId);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/scope1/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Upload failed');
      }
      setUploadedInvoice(await res.json());
      setFile(picked);
    } catch (err) {
      setUploadError(t('entry.uploadFailed', { message: err.message }));
    } finally {
      setUploading(false);
    }
  };

  // In distance mode the calculation uses distanceVehicleType — fuelKey is not required
  const isSaveDisabled = !formData.buildingId || !formData.vehicleCategory ||
    (registrationMethod === 'fuel' && !formData.fuelKey) ||
    !formData.period || !formData.quantity || parseFloat(formData.quantity) <= 0 ||
    saving || isSaved || formData.vehicleCategory === 'ev';

  const isBusy = saving;
  
  // Vehicle type checks
  const isEV = formData.vehicleCategory === 'ev';
  const isHybrid = formData.vehicleCategory === 'hybrid';

  const unitLabel = registrationMethod === 'distance'
    ? 'km'
    : (formData.unit === 'l' ? t('s1mob.litres') : formData.unit === 'm3' ? 'm³' : formData.unit);

  const selectedFuel = fuels.find(f => f.key === formData.fuelKey);

  return (
    <ErrorBoundary>
      <Shell>
        <div className="k2-wrap">
          {isBusy && <Loader />}

          <PageHeader
            title={t('mobile.title')}
            desc={t('mobile.desc')}
          >
            <Button variant="gh" icon={X.Doc} onClick={() => navigate('/records')}>
              {t('entry.viewRecords')}
            </Button>
          </PageHeader>

          <EntryStepper
            scope={1}
            step={step}
            setStep={setStep}
            facilities={buildings}
            siteId={formData.buildingId}
            onSite={(e) => { handleInputChange('buildingId', e.target.value); setBuildingValidationError(''); }}
            siteError={buildingValidationError}
            /* scope1_mobile_records has no activity_purpose column — the selector is
               omitted rather than collecting something the API would silently drop. */
            purposes={[]}
            period={formData.period}
            mode={mode}
            setMode={setMode}
            ocrSupported={false}
            docLabel={t('stationary.doc')}
            file={file}
            onFile={handlePickFile}
            uploading={uploading}
            uploadNote={
              uploadError ? (
                <div style={{ marginTop: 10 }}><Callout tone="err">{uploadError}</Callout></div>
              ) : uploadedInvoice ? (
                <div style={{ marginTop: 10 }}><Callout tone="ok">{t('entry.uploadOk')}</Callout></div>
              ) : null
            }
            onCalculate={handleSaveEmissions}
            calcDisabled={isSaveDisabled}
            calcLabel={t(saving ? 'entry.calculating' : 'entry.calc')}
            reviewRows={[
              { label: t('entry.building'), value: (buildings.find(b => String(b.id) === String(formData.buildingId)) || {}).name || '—' },
              { label: t('mobile.category'), value: (VEHICLE_CATEGORIES.find(v => v.value === formData.vehicleCategory) || {}).label || formData.vehicleCategory || '—' },
              vehicleId ? { label: t('mobile.vehicle'), value: vehicleId } : null,
              registrationMethod === 'distance'
                ? { label: t('entry.method'), value: `${t('mobile.distanceMethod')} · ${(DISTANCE_VEHICLE_TYPES.find(d => d.value === distanceVehicleType) || {}).label || ''}` }
                : { label: t('mobile.fuelType'), value: (selectedFuel || {}).label || MOBILE_FUEL_LABELS[formData.fuelKey] || formData.fuelKey || '—' },
              { label: t('stationary.quantity'), value: `${formData.quantity} ${unitLabel}` },
              { label: t('entry.period'), value: formData.period },
              { label: t('entry.document'), value: file ? file.name : t('entry.notAttached') }
            ]}
            result={{ value: calculated ? Number(calculated.emissions_tCO2e).toFixed(3) : '0.000', unit: t('common.tco2e') }}
            saving={saving}
            error={flowError}
            onSave={handleFlowSave}
            saved={isSaved}
            successText={calculated
              ? t('mobile.success', { value: Number(calculated.emissions_tCO2e).toFixed(3), period: formData.period })
              : ''}
            coverage={buildCoverageChips('scope1', coverage)}
            nextStep={nextStepFor('scope1')}
            onAddAnother={handleFlowAddAnother}
            onFinish={handleFlowFinish}
            successExtra={showHybridScope2Modal && (
              <Callout tone="info" center>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span>{t('mobile.hybridPrompt')}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <Button size="sm" variant="mint" onClick={() => navigate('/scope-2/electricity')}>
                      {t('mobile.hybridAction')}
                    </Button>
                    <Button size="sm" variant="gh" onClick={() => setShowHybridScope2Modal(false)}>
                      {t('mobile.later')}
                    </Button>
                  </span>
                </div>
              </Callout>
            )}
            context={
              <>
                {isHybrid && hybridCombined && (hybridCombined.scope1Fuel_tCO2e > 0 || hybridCombined.scope2Ev_tCO2e > 0) && (
                  <Card title={t('mobile.combined')} sub={`${t('entry.period')} ${formData.period}`}>
                    <DList rows={[
                      [t('mobile.combinedFuel'), `${hybridCombined.scope1Fuel_tCO2e.toFixed(3)} t CO₂e`],
                      [t('mobile.combinedCharge'), `${hybridCombined.scope2Ev_tCO2e.toFixed(3)} t CO₂e`],
                      [t('mobile.combinedTotal'), `${hybridCombined.combined_tCO2e.toFixed(3)} t CO₂e`]
                    ]} />
                    {hybridCombined.scope2Ev_tCO2e === 0 && (
                      <div style={{ marginTop: 10 }}>
                        <Callout tone="warn">
                          {t('mobile.noCharging')}
                        </Callout>
                      </div>
                    )}
                  </Card>
                )}
                <CategoryContext
                  recordTypes={['scope1-mobile']}
                  categoryLabel={t('mobile.title')}
                  facilities={buildings}
                  refreshKey={ledgerKey}
                  onPickSite={(id) => { handleInputChange('buildingId', String(id)); setStep(1); }}
                />
                <MonthLedger
                  period={formData.period}
                  recordTypes={['scope1-mobile']}
                  buildings={buildings}
                  refreshKey={ledgerKey}
                  emptyHint={t('mobile.ledgerEmpty')}
                  onChanged={() => { fetchSummary(formData.period); setLedgerKey(k => k + 1); }}
                />
              </>
            }
          >
            {/* ── vehicle ── */}
            <div className="k2-g2">
              <Field label={t('mobile.vehicleType')} required>
                <Select value={formData.vehicleCategory} onChange={(e) => handleVehicleCategoryChange(e.target.value)}>
                  {VEHICLE_CATEGORIES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </Select>
              </Field>

              <Field label={t('mobile.vehicleId')} optional hint={existingVehicles.length > 0
                ? t('mobile.registered', { list: existingVehicles.map(v => (v.vehicleId || v.vehicleType)).join(', ') })
                : undefined}>
                <Input
                  type="text"
                  list="vehicle-list"
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  placeholder={t('mobile.vehicleIdPlaceholder')}
                />
                <datalist id="vehicle-list">
                  {existingVehicles.map(v => (
                    <option key={(v.vehicleId || v.vehicleType)} value={(v.vehicleId || v.vehicleType)} />
                  ))}
                </datalist>
              </Field>
            </div>

            {isEV ? (
              <Callout tone="warn" center>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span>
                    {t('mobile.evNote')}
                  </span>
                  <Button size="sm" variant="mint" style={{ marginLeft: 'auto' }} onClick={() => navigate('/scope-2/electricity')}>
                    {t('mobile.evAction')}
                  </Button>
                </div>
              </Callout>
            ) : (
              <>
                {isHybrid && (
                  <Callout tone="info">
                    {t('mobile.hybridNote')}
                  </Callout>
                )}

                <Field label={t('mobile.method')} hint={t('mobile.methodHint')}>
                  <div className="k2-bgroup" style={{ width: '100%' }}>
                    <Button
                      className={registrationMethod === 'fuel' ? 'k2-on' : undefined}
                      style={{ flex: 1 }}
                      onClick={() => handleMethodChange('fuel')}
                    >
                      {t('mobile.byFuel')}
                    </Button>
                    <Button
                      className={registrationMethod === 'distance' ? 'k2-on' : undefined}
                      style={{ flex: 1 }}
                      onClick={() => handleMethodChange('distance')}
                    >
                      {t('mobile.byDistance')}
                    </Button>
                  </div>
                </Field>

                <div className="k2-g2">
                  {registrationMethod === 'fuel' ? (
                    <Field label={t('mobile.fuelType')} required>
                      <Select value={formData.fuelKey} onChange={(e) => handleFuelChange(e.target.value)}>
                        <option value="">{t('mobile.selectFuel')}</option>
                        {fuels.map(f => (
                          <option key={f.key} value={f.key}>{MOBILE_FUEL_LABELS[f.key] || f.label}</option>
                        ))}
                      </Select>
                    </Field>
                  ) : (
                    <Field label={t('mobile.distanceType')} required>
                      <Select value={distanceVehicleType} onChange={(e) => setDistanceVehicleType(e.target.value)}>
                        {DISTANCE_VEHICLE_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                      </Select>
                    </Field>
                  )}

                  <Field label={t('mobile.month')} required>
                    <Input type="month" value={formData.period} onChange={(e) => handleInputChange('period', e.target.value)} />
                  </Field>
                </div>

                <Field label={t('stationary.quantity')} required>
                  <UnitInput
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', e.target.value)}
                    unit={unitLabel}
                  />
                </Field>
              </>
            )}
          </EntryStepper>
        </div>
      </Shell>
    </ErrorBoundary>
  );
};
